import React, { useEffect, useState } from "react";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { Bot, Mail, MessageCircle, Phone, Globe, Webhook, UserRound, Sparkles, Check, CheckCheck, Clock, AlertCircle } from "lucide-react";
import { T, TYPE, formatDuration } from "../user/complaints/shared";

// Small helpdesk-specific visual pieces. Colours for statuses/priorities come
// from the database (admin-configurable), so chips derive tints from the hex.

const hexToRgba = (hex, a) => {
  const h = String(hex || "#667085").replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

export const ColorChip = ({ label, color, size = "small", sx }) => (
  <Chip
    size={size}
    label={
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: color || T.inkMuted, flexShrink: 0 }} />
        <span>{label || "—"}</span>
      </Stack>
    }
    sx={{
      bgcolor: hexToRgba(color, 0.12), color: T.ink, fontWeight: 600, fontSize: TYPE.micro, height: 24,
      borderRadius: "999px", border: `1px solid ${hexToRgba(color, 0.25)}`, "& .MuiChip-label": { px: 1 }, ...sx,
    }}
  />
);

export const StatusBadge = ({ status }) => <ColorChip label={status?.name} color={status?.color} />;
export const PriorityBadge = ({ priority }) => <ColorChip label={priority?.name} color={priority?.color} />;

const useNow = (ms = 30000) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
};

const SLA_STYLE = {
  on_track: { color: "#067647", bg: "#DCFAE6", label: "On track" },
  at_risk: { color: "#B54708", bg: "#FEF0C7", label: "At risk" },
  breached: { color: "#B42318", bg: "#FEE4E2", label: "Breached" },
  paused: { color: "#475467", bg: "#F2F4F7", label: "Paused" },
  none: { color: "#98A2B3", bg: "#F9FAFB", label: "No SLA" },
};

/** Live countdown to the next SLA deadline (first response, then resolution). */
export const SlaBadge = ({ ticket, compact = false }) => {
  const now = useNow();
  const sla = ticket?.sla || {};
  const closed = ["resolved", "closed"].includes(ticket?.statusType);
  let state = ticket?.slaState || "none";
  let label = SLA_STYLE[state]?.label;
  let due = null;
  let what = "";
  if (!closed && state !== "paused" && state !== "none") {
    if (!sla.firstRespondedAt && sla.firstResponseDueAt) {
      due = new Date(sla.firstResponseDueAt);
      what = "First response";
    } else if (sla.resolutionDueAt) {
      due = new Date(sla.resolutionDueAt);
      what = "Resolution";
    }
    if (due) {
      const mins = (due.getTime() - now) / 60000;
      if (mins < 0) state = "breached";
      label = mins < 0 ? `${formatDuration(-mins)} overdue` : `${formatDuration(mins)} left`;
    }
  }
  if (closed) {
    state = sla.resolutionBreachedAt ? "breached" : sla.policyId ? "on_track" : "none";
    label = sla.resolutionBreachedAt ? "Breached" : sla.policyId ? "Met" : "No SLA";
  }
  const s = SLA_STYLE[state] || SLA_STYLE.none;
  const chip = (
    <Chip
      size="small"
      icon={<Clock size={12} color={s.color} />}
      label={compact ? label : `${what ? `${what}: ` : ""}${label}`}
      sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600, fontSize: TYPE.micro, height: 24, borderRadius: "999px", "& .MuiChip-icon": { ml: "6px" } }}
    />
  );
  return due ? <Tooltip title={`${what} due ${due.toLocaleString("en-IN")}`}>{chip}</Tooltip> : chip;
};

export const CHANNEL_ICON = { email: Mail, chat: MessageCircle, voice: Phone, whatsapp: MessageCircle, web: Globe, api: Webhook };

export const ChannelIcon = ({ channel, size = 14 }) => {
  const Icon = CHANNEL_ICON[channel] || Globe;
  return (
    <Tooltip title={channel}>
      <Box component="span" sx={{ display: "inline-flex", color: T.inkSecondary }}>
        <Icon size={size} />
      </Box>
    </Tooltip>
  );
};

/** "AI Handling" / "Agent Handling" / "AI Assist Mode" — always visible in chats. */
export const ModeBadge = ({ mode, status }) => {
  const cfg =
    mode === "ai"
      ? { icon: Bot, label: "AI Handling", color: "#5925DC", bg: "#F4EBFF" }
      : mode === "ai_assist"
        ? { icon: Sparkles, label: "AI Assist Mode", color: "#026AA2", bg: "#E0F2FE" }
        : status === "queued"
          ? { icon: Clock, label: "Waiting for agent", color: "#B54708", bg: "#FEF0C7" }
          : { icon: UserRound, label: "Agent Handling", color: "#067647", bg: "#DCFAE6" };
  const Icon = cfg.icon;
  return (
    <Chip size="small" icon={<Icon size={13} color={cfg.color} />} label={cfg.label}
      sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: TYPE.micro, height: 24, "& .MuiChip-icon": { ml: "6px" } }} />
  );
};

const PRESENCE_COLOR = { online: "#17B26A", away: "#F79009", busy: "#F04438", offline: "#98A2B3" };
export const PresenceDot = ({ presence, size = 9 }) => (
  <Tooltip title={presence || "offline"}>
    <Box component="span" sx={{ display: "inline-block", width: size, height: size, borderRadius: "50%", bgcolor: PRESENCE_COLOR[presence] || PRESENCE_COLOR.offline, border: "2px solid #fff", boxShadow: "0 0 0 1px #E4E7EC" }} />
  </Tooltip>
);

/** Sent / Delivered / Read / Failed tick for outgoing messages. */
export const MessageTicks = ({ status }) => {
  if (status === "failed") return <Tooltip title="Failed"><AlertCircle size={13} color="#F04438" /></Tooltip>;
  if (status === "sending") return <Tooltip title="Sending"><Clock size={12} color={T.inkMuted} /></Tooltip>;
  if (status === "read") return <Tooltip title="Read"><CheckCheck size={14} color="#2E90FA" /></Tooltip>;
  if (status === "delivered") return <Tooltip title="Delivered"><CheckCheck size={14} color={T.inkMuted} /></Tooltip>;
  return <Tooltip title="Sent"><Check size={14} color={T.inkMuted} /></Tooltip>;
};

export const SentimentChip = ({ sentiment }) => {
  if (!sentiment) return null;
  const c = { positive: ["#067647", "#DCFAE6"], neutral: ["#475467", "#F2F4F7"], negative: ["#B42318", "#FEE4E2"] }[sentiment] || ["#475467", "#F2F4F7"];
  return <Chip size="small" label={sentiment} sx={{ bgcolor: c[1], color: c[0], fontWeight: 600, fontSize: TYPE.micro, height: 22, textTransform: "capitalize" }} />;
};

export const SmallLabel = ({ children, sx }) => (
  <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.4px", textTransform: "uppercase", color: T.inkMuted, ...sx }}>{children}</Typography>
);

export const fileSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export const uid = () => `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

export const EVENT_TEXT = {
  assigned: (d) => (d.to ? `Assigned to ${d.to}${d.by ? ` by ${d.by}` : ""}` : `Unassigned${d.by ? ` by ${d.by}` : ""}`),
  status_changed: (d) => `Status changed ${d.from} → ${d.to}${d.by ? ` by ${d.by}` : ""}${d.reason ? ` — ${d.reason}` : ""}`,
  handoff_requested: (d) => `Human handoff requested (${String(d.reason || "").replace(/_/g, " ")})`,
  accepted: (d) => `${d.agent} accepted the chat`,
  takeover: (d) => `${d.agent} took over from ${d.fromMode === "ai" ? "the AI" : "the queue"} — AI stopped`,
  returned_to_ai: (d) => `${d.by} returned the chat to the AI`,
  assist_on: (d) => `${d.agent} turned on AI Assist`,
  assist_off: (d) => `${d.agent} turned off AI Assist`,
  transferred: (d) => `Transferred${d.to ? ` to ${d.to}` : " to another team"} by ${d.by}${d.reason ? ` — ${d.reason}` : ""}`,
  merged: (d) => `${d.source} was merged into this ticket by ${d.by}`,
  chat_attached: () => "Customer opened live chat — attached to this ticket",
  chat_linked: (d) => `Chat linked to this ticket by ${d.by}`,
  follow_up: (d) => `Follow-up of closed ticket ${d.of}`,
  escalated: (d) => `Escalated by ${d.by}`,
  reply_on_closed: () => "Customer replied on a closed ticket (reopen disabled)",
  ai_email_skipped: (d) => `AI did not auto-reply (confidence ${Math.round((d.confidence || 0) * 100)}%)`,
  attachments_rejected: () => "Some attachments were rejected",
  resolved: () => "Resolved",
};
export const eventText = (e) => (EVENT_TEXT[e?.name] ? EVENT_TEXT[e.name](e.data || {}) : String(e?.name || "").replace(/_/g, " "));
